import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import { CircleNotch, ArrowLeft, FloppyDisk, CheckCircle, Plus, Trash } from "@phosphor-icons/react";
import CoPilotPanel, { CoPilotToggle } from "@/components/CoPilotPanel";
import { AnsoffGrid } from "@/components/blueprints/AnsoffBlueprint";

const uid = () => "an_" + Math.random().toString(36).slice(2, 10);
const inputCls = "flex h-10 w-full rounded-sm border border-slate-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500";
const selCls = "h-10 rounded-sm border border-slate-300 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500";

export default function Ansoff() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState("");
  const [context, setContext] = useState("");
  const [initiatives, setInitiatives] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try { const res = await api.get(`/documents/${id}`); setTitle(res.data.title || ""); setContext(res.data.payload?.context || ""); setInitiatives(res.data.payload?.initiatives || []); }
      catch { toast.error("Document not found"); navigate("/dashboard"); }
      finally { setLoading(false); }
    })();
  }, [id]); // eslint-disable-line

  const persist = useCallback(async (extra = {}) => {
    setSaving(true);
    try { await api.put(`/documents/${id}`, { title: title || "Untitled Ansoff Matrix", payload: { context, initiatives }, ...extra }); return true; }
    catch { toast.error("Save failed"); return false; }
    finally { setSaving(false); }
  }, [id, title, context, initiatives]);

  const addItem = () => setInitiatives((x) => [...x, { id: uid(), name: "", market: "existing", product: "existing", note: "" }]);
  const setItem = (i, patch) => setInitiatives((x) => x.map((it, j) => (j === i ? { ...it, ...patch } : it)));
  const removeItem = (i) => setInitiatives((x) => x.filter((_, j) => j !== i));
  const saveDraft = async () => { if (await persist()) toast.success("Draft saved"); };
  const finalise = async () => { if (await persist({ status: "Completed" })) { toast.success("Matrix finalised"); navigate(`/ansoff/${id}/view`); } };

  if (loading) return <Layout><div className="flex items-center justify-center py-40 text-slate-400"><CircleNotch size={28} className="animate-spin" /></div></Layout>;

  return (
    <Layout>
      <div className="max-w-5xl mx-auto w-full py-8 px-6 lg:px-10">
        <button data-testid="back-link" onClick={() => navigate("/dashboard")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-3"><ArrowLeft size={16} /> Workspace</button>
        <input data-testid="ansoff-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ansoff matrix title…" className="w-full bg-transparent text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 placeholder:text-slate-300 focus:outline-none border-b border-transparent focus:border-slate-300 pb-1" />
        <textarea data-testid="ansoff-context-input" rows={2} value={context} onChange={(e) => setContext(e.target.value)} placeholder="Context — the growth question you are weighing…" className="mt-3 w-full rounded-sm border border-slate-300 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500" />

        <div className="mt-6 bg-white border border-slate-200 rounded-sm shadow-sm p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div><h2 className="text-xl font-semibold text-slate-900">Growth Initiatives</h2><p className="text-sm text-slate-500">Tag each initiative by market and product — it maps onto the matrix instantly.</p></div>
            <button data-testid="ansoff-add-item" onClick={addItem} className="inline-flex items-center gap-1.5 rounded-sm border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"><Plus size={16} /> Add initiative</button>
          </div>
          {initiatives.length === 0 && <p className="text-sm text-slate-400 border border-dashed border-slate-300 rounded-sm p-4 text-center">Add growth initiatives to begin.</p>}
          <div className="space-y-2">
            {initiatives.map((it, i) => (
              <div key={it.id} data-testid={`ansoff-item-${i}`} className="flex flex-col lg:flex-row lg:items-center gap-2 border border-slate-200 rounded-sm p-3">
                <input data-testid={`ansoff-name-${i}`} className={inputCls + " lg:flex-1"} value={it.name} onChange={(e) => setItem(i, { name: e.target.value })} placeholder="Initiative" />
                <div className="flex items-center gap-2">
                  <span className="text-xs font-mono text-slate-400">Market</span>
                  <select data-testid={`ansoff-market-${i}`} className={selCls} value={it.market} onChange={(e) => setItem(i, { market: e.target.value })}>
                    <option value="existing">Existing</option><option value="new">New</option>
                  </select>
                  <span className="text-xs font-mono text-slate-400">Product</span>
                  <select data-testid={`ansoff-product-${i}`} className={selCls} value={it.product} onChange={(e) => setItem(i, { product: e.target.value })}>
                    <option value="existing">Existing</option><option value="new">New</option>
                  </select>
                </div>
                <input data-testid={`ansoff-note-${i}`} className={inputCls + " lg:w-56"} value={it.note} onChange={(e) => setItem(i, { note: e.target.value })} placeholder="Note (optional)" />
                <button data-testid={`ansoff-remove-${i}`} onClick={() => removeItem(i)} className="p-2 text-slate-400 hover:text-red-600 self-end lg:self-auto"><Trash size={16} /></button>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-6 bg-white border border-slate-200 rounded-sm shadow-sm p-6">
          <h2 className="text-sm font-mono font-bold uppercase tracking-[0.1em] text-slate-700 mb-4">Live Matrix</h2>
          <AnsoffGrid initiatives={initiatives} />
        </div>

        <div className="mt-6 flex items-center justify-end gap-3 sticky bottom-0 bg-slate-50 py-4">
          <button data-testid="ansoff-save-draft" onClick={saveDraft} disabled={saving} className="inline-flex items-center gap-2 rounded-sm border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50">{saving ? <CircleNotch size={16} className="animate-spin" /> : <FloppyDisk size={16} />} Save Draft</button>
          <button data-testid="ansoff-finalise" onClick={finalise} disabled={saving} className="inline-flex items-center gap-2 rounded-sm bg-emerald-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-emerald-700"><CheckCircle size={18} weight="fill" /> Finalise Matrix</button>
        </div>
      </div>
      <CoPilotToggle open={open} onClick={() => setOpen(true)} phase="STRATEGY" />
      <CoPilotPanel open={open} onClose={() => setOpen(false)} docId={id} toolType="ansoff" step={{ label: "Ansoff Matrix" }} phase="STRATEGY" onApplySuggestion={(t) => { navigator.clipboard?.writeText(t); toast.info("Copied to clipboard"); }} />
    </Layout>
  );
}
